import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Check, Info, Calculator } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">
            {isTeacher ? "Ücret Bilgisi" : "Fiyatlandırma"}
          </span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="pricing-title">
            {isTeacher ? "Ders Ücretleri" : "Sade ve şeffaf ders ücretleri"}
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            {isTeacher
              ? "Aileden alacağın saatlik ücretler aşağıdaki gibidir. Alt kısımda platform komisyonuna dair örnek hesaplamayı bulabilirsin."
              : "Ödeme sizinle öğretmen arasında; biz sadece doğru eşleşmeyi sağlıyoruz."}
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 reveal reveal-delay-1">
          <div className="rounded-2xl p-10 relative overflow-hidden border-2 border-[color:var(--brand)]/30 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid="pricing-in-person" style={{background: "linear-gradient(135deg, #FDE8D6 0%, #FAD3B4 100%)"}}>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white font-semibold bg-[color:var(--brand)] rounded-full px-3 py-1">
              ★ Popüler · Yüz Yüze
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-6xl font-semibold text-slate-900">1500</span>
              <span className="text-slate-700 text-lg">TL / saat</span>
            </div>
            <ul className="mt-8 space-y-3 text-sm text-slate-800">
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Öğrencinin adresine gelen öğretmenler</li>
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Yüz yüze etkileşim, daha yüksek odak</li>
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Materyaller birlikte kullanılabilir</li>
            </ul>
          </div>

          <div className="rounded-2xl p-10 relative overflow-hidden border border-[color:var(--brand)]/20 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="pricing-online" style={{background: "linear-gradient(135deg, #EEF0F7 0%, #DDE1F0 100%)"}}>
            <div className="inline-block text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold bg-[color:var(--brand)]/10 rounded-full px-3 py-1">Online</div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-6xl font-semibold text-slate-900">1200</span>
              <span className="text-slate-600 text-lg">TL / saat</span>
            </div>
            <ul className="mt-8 space-y-3 text-sm text-slate-800">
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Konumdan bağımsız, esnek saatler</li>
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Video, ekran paylaşımı, dijital tahta</li>
              <li className="flex gap-2"><Check size={16} className="text-[color:var(--brand)] mt-0.5" /> Trafik veya seyahat kaygısı yok</li>
            </ul>
          </div>
        </div>

        {isTeacher && (
          <div className="mt-12 border border-[color:var(--accent)]/30 rounded-2xl overflow-hidden reveal reveal-delay-2" data-testid="commission-block">
            <div className="bg-[color:var(--accent)]/10 px-6 sm:px-8 py-4 flex items-center gap-2 border-b border-[color:var(--accent)]/20">
              <Calculator size={18} className="text-[color:var(--accent)]" />
              <div className="font-display text-lg font-semibold text-slate-900">%20 Platform Komisyonu</div>
            </div>
            <div className="p-6 sm:p-8 space-y-4 text-sm sm:text-base text-slate-700 leading-relaxed bg-white">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-[color:var(--accent)] mt-1 flex-shrink-0" />
                <p>
                  Aileden alacağın ders ücreti brüt tutardır. Bunun <strong className="text-slate-900">%20'si platform komisyonu</strong> olarak tarafımıza aktarılır, kalan %80'i tamamen senin net kazancındır.
                </p>
              </div>
              <div className="rounded-xl bg-[color:var(--bg-2)] p-5 border border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Örnek hesaplama</div>
                <p className="mt-2 text-slate-800">
                  Ayda <strong>8 saat</strong> yüz yüze derse giderseniz, aileden alacağınız toplam ücret{" "}
                  <strong className="text-[color:var(--brand)]">12.000 TL</strong>'dir. Bunun{" "}
                  <strong>%20'si olan 2.400 TL</strong> platform komisyonu olarak tarafımıza gönderilir.{" "}
                  Geri kalan <strong className="text-[color:var(--mint)]">9.600 TL</strong> ise tamamen sizin{" "}
                  <strong>net kazancınızdır</strong>.
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <MiniStat label="Brüt" value="12.000 TL" />
                  <MiniStat label="Komisyon" value="2.400 TL" accent="var(--accent)" />
                  <MiniStat label="Net" value="9.600 TL" accent="var(--mint)" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
      <div className="mt-1 font-display text-lg font-semibold" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
